import React, { useEffect, useState } from 'react';
import { Card, CardTitle, CardBody, Button, Table, Input } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../constants/ApiService';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { useSelector } from 'react-redux';
import conditionalDataLoad from '../../utils/conditionalDataLoad';
import useVendorBranch from '../../utils/useVendorBranch';
import { toast } from 'react-toastify';

const StudentHostelAssignList = () => {
  const { user } = useSelector((state) => state.authState);
  const navigate = useNavigate();

  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);
  const [search, setSearch] = useState('');
  const [errorMessage, setErrorMessage] = useState({});
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
  });

  // ✅ filters (optional)
  const [studentSearch, setStudentSearch] = useState('');
  const [hostelSearch, setHostelSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const onHandleAdd = () => {
    navigate('/save-hostel-mapping');
  };

  const onHandleEdit = (row) => {
    navigate('/save-hostel-mapping', { state: { ...(row || {}) } });
  };

  const onHandleDelete = async (id) => {
    try {
      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      if (!id) return;
      if (!window.confirm('Are you sure you want to delete this mapping?'))
        return;

      const res = await apiService.delete(
        `api/HostelMaster/DeleteHostelMapping?id=${id}&vendorId=${vendorId}&branchId=${branchId}`,
      );

      if (res?.status === true) {
        toast.success(res.message || 'Deleted');
        GetMappingList();
      } else {
        toast.error(res?.message || 'Failed');
      }
    } catch (e) {
      console.error(e);
      toast.error('Something went wrong');
    }
  };

  const GetMappingList = async () => {
    try {
      setLoading(true);

      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      const qs =
        `vendorId=${encodeURIComponent(vendorId)}` +
        `&branchId=${encodeURIComponent(branchId)}` +
        (studentSearch ? `&search=${encodeURIComponent(studentSearch)}` : '') +
        (hostelSearch
          ? `&hostelSearch=${encodeURIComponent(hostelSearch)}`
          : '') +
        (fromDate ? `&fromDate=${encodeURIComponent(fromDate)}` : '') +
        (toDate ? `&toDate=${encodeURIComponent(toDate)}` : '');

      const res = await apiService.get(
        `api/HostelMaster/GetAllAssignedHostel?${qs}`,
      );

      if (res?.status === true) {
        setList(res.data || res.Data || []);
      } else {
        setList([]);
        toast.error(res?.message || 'Failed');
      }
    } catch (e) {
      console.error(e);
      setList([]);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // vendor/branch change
  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });

    if (canLoad) GetMappingList();
    else setList([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionData.vendor_id, selectionData.branch_id]);

  // debounce filters
  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });
    if (!canLoad) return;

    const t = setTimeout(() => {
      GetMappingList();
    }, 400);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentSearch, hostelSearch, fromDate, toDate]);

  const onHandleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    GetMappingList(val);
  };
  return (
    <>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <div className='d-flex gap-2'></div>

        <Button color='primary' onClick={onHandleAdd}>
          <i className='bi bi-plus-circle' /> Assign Hostel
        </Button>
      </div>

      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />

      <Card className='card-box pd-20 mb-20'>
        <CardTitle className='p-3 mb-0'>
          <i className='bi bi-house-door mr-2' /> Student Hostel Mapping
        </CardTitle>
        <div className='p-2'>
          <Input
            placeholder='Search Holiday...'
            value={search}
            onChange={onHandleSearch}
          />
        </div>
        <CardBody>
          <div className='table-responsive'>
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th style={{ width: 160 }}>Action</th>
                  <th>Building</th>
                  <th>Room</th>
                  <th style={{ width: 110 }}>From</th>
                  <th style={{ width: 110 }}>To</th>
                  <th style={{ width: 90 }}>Status</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan='10' className='text-center'>
                      Loading...
                    </td>
                  </tr>
                ) : list?.length > 0 ? (
                  list.map((x, i) => (
                    <tr key={x.id || i}>
                      <td>{i + 1}</td>

                      <td>
                        <Button
                          size='sm'
                          color='link'
                          className='p-0 me-2 text-primary'
                          title='Edit'
                          onClick={() => onHandleEdit(x)}
                        >
                          <i className='bi bi-pencil-square fs-5' />
                        </Button>

                        <Button
                          size='sm'
                          color='link'
                          className='p-0 text-danger'
                          title='Delete'
                          onClick={() => onHandleDelete(x.id)}
                        >
                          <i className='bi bi-trash fs-5' />
                        </Button>
                      </td>

                      <td>{x.building_name}</td>
                      <td>{x.room_name}</td>

                      <td>
                        {x.from_date ? String(x.from_date).slice(0, 10) : ''}
                      </td>
                      <td>{x.to_date ? String(x.to_date).slice(0, 10) : ''}</td>

                      <td>
                        {String(x.status).toLowerCase() === 'active' ? (
                          <span className='text-success'>Active</span>
                        ) : String(x.status).toLowerCase() === 'deleted' ? (
                          <span className='text-muted'>Deleted</span>
                        ) : (
                          <span className='text-danger'>Inactive</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan='10' className='text-center'>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default StudentHostelAssignList;
